import React from 'react';
import graphql from 'babel-plugin-relay/macro';
import { useLazyLoadQuery } from 'react-relay';
import { ParentPlanSelection, useSegment } from '@sparknz/journeys-common';

import { ChangePlanParentPlanSelectionContentQuery } from '__generated__/ChangePlanParentPlanSelectionContentQuery.graphql';
import { useChangePlanActionLayer } from 'components/shop/MobilePlans/hooks/useChangePlanActionLayer';
import GenericErrorBanner from '../../../GenericErrorBanner';

const query = graphql`
  query ChangePlanParentPlanSelectionContentQuery(
    $offerId: String!
    $sourceOfferId: String!
    $lineNumber: String!
    $channel: CartChannel!
  ) {
    changePlanEligibility(
      input: {
        offerId: $offerId
        sourceOfferId: $sourceOfferId
        lineNumber: $lineNumber
        channel: $channel
      }
    ) {
      eligibleAccounts {
        accountNumber
        accountName
        eligibilityCountAvailable
        eligibilityCountMaximum
        eligibleLines {
          lineNumber
          offerId
          offerName
        }
        ineligibleLines {
          lineNumber
          offerId
          offerName
          reason
        }
      }
      ineligibleAccounts {
        accountNumber
        accountName
        eligibilityCountAvailable
        eligibilityCountMaximum
        reason
      }
    }
  }
`;

interface ChangePlanParentPlanSelectionContentInnerProps {
  offerId: string;
  sourceOfferId: string;
  lineNumber: string;
  redirectBaseUrl: string;
  selectedAccountNumber?: string;
  hasMultipleAccounts: boolean;
  closeActionLayer(): void;
  navigateToAccountSelect(): void;
}

const buildUrl = (baseUrl: string, params: Record<string, string>) => {
  const url = new URL(baseUrl, window.location.origin);

  Object.keys(params).forEach((key) =>
    url.searchParams.append(key, params[key]),
  );

  return url.href;
};

const ChangePlanParentPlanSelectionContentInner = ({
  offerId,
  sourceOfferId,
  lineNumber,
  redirectBaseUrl,
  selectedAccountNumber,
  hasMultipleAccounts,
  closeActionLayer,
  navigateToAccountSelect,
}: ChangePlanParentPlanSelectionContentInnerProps) => {
  const { channel } = useSegment();

  const {
    changePlanEligibility: { eligibleAccounts, ineligibleAccounts },
  } = useLazyLoadQuery<ChangePlanParentPlanSelectionContentQuery>(
    query,
    {
      offerId,
      sourceOfferId,
      lineNumber,
      channel,
    },
    { fetchPolicy: 'network-only' },
  );

  const navigateToMySpark = (parentLineNumber: string) => {
    const url = buildUrl(redirectBaseUrl, {
      action: 'changePlan',
      offerId,
      lineNumber,
      parentLineNumber,
    });

    window.location.assign(url);
  };

  const availableLines = eligibleAccounts[0]?.eligibleLines ?? [];
  const isSelectedLineAlreadyParent = eligibleAccounts[0]?.ineligibleLines.some(
    (ineligibleLine) => {
      return (
        ineligibleLine.lineNumber === lineNumber &&
        ineligibleLine.reason === 'UNSATISFIED_DEPENDENCY'
      );
    },
  );
  const ineligibilityStatus = isSelectedLineAlreadyParent
    ? 'UNSATISFIED_DEPENDENCY'
    : ineligibleAccounts[0]?.reason ?? null;

  const accounts = [...eligibleAccounts, ...ineligibleAccounts];
  const selectedAccount = accounts.find(
    ({ accountNumber }) => accountNumber === selectedAccountNumber,
  );

  if (availableLines.length === 1 && !isSelectedLineAlreadyParent) {
    navigateToMySpark(availableLines[0].lineNumber);
    return null;
  }

  return (
    <ParentPlanSelection
      lines={isSelectedLineAlreadyParent ? [] : availableLines}
      ineligibilityStatus={ineligibilityStatus}
      selectedAccount={selectedAccount}
      onSubmit={navigateToMySpark}
      onClose={closeActionLayer}
      {...(hasMultipleAccounts
        ? { onChangeAccountClick: navigateToAccountSelect }
        : {})}
    />
  );
};

const ChangePlanParentPlanSelectionContent = () => {
  const {
    accounts,
    showActionLayer,
    redirect,
    selectedLine,
    selectedAccount,
    ...handlers
  } = useChangePlanActionLayer();

  const hasMultipleAccounts = (accounts ?? []).length > 1;

  if (!showActionLayer) {
    return null;
  }

  if (redirect && selectedLine) {
    return (
      <ChangePlanParentPlanSelectionContentInner
        {...handlers}
        offerId={redirect.planId}
        sourceOfferId={selectedLine.offerId}
        lineNumber={selectedLine.lineNumber}
        redirectBaseUrl={redirect.path}
        selectedAccountNumber={selectedAccount}
        hasMultipleAccounts={hasMultipleAccounts}
      />
    );
  }

  return <GenericErrorBanner />;
};

export default ChangePlanParentPlanSelectionContent;
